import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {MetaInsight} from "crqlar-react-common/dist/models/meta/metaInsight";
import {MetaInsightsService} from "crqlar-react-common/dist/services/meta/metaInsights.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useFacebookConfig} from "crqlar-react-common/dist/utils/hooks/meta/useFacebookConfig";
import {useInsightFilterOptions} from "crqlar-react-common/dist/utils/hooks/meta/useMetaFilterOptions";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    RightSidebarActionButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";

function MetaInsightStatusCell({status}: {status?: string}) {
    if (!status) return null;

    return (
        <Box
            role="img"
            aria-label={status}
            sx={{
                width: 20,
                height: 20,
                bgcolor: status === "ACTIVE" ? "success.main" : "error.main",
                borderRadius: 1,
            }}
        />
    );
}

function MetaInsightMetricCell({value, suffix}: {
    value?: string
    suffix?: string
}) {
    if (value === undefined || value === null || value === "") return null;

    return (
        <Box sx={{fontVariantNumeric: "tabular-nums"}}>
            {value}{suffix}
        </Box>
    );
}

function MetaInsightCurrencyCell({symbol, value, suffix}: {
    symbol?: string
    value?: string
    suffix?: string
}) {
    if (value === undefined || value === null || value === "") return null;

    return (
        <Box sx={{fontVariantNumeric: "tabular-nums"}}>
            {symbol ? `${symbol} ` : null}{value}{suffix}
        </Box>
    );
}

export default function InsightsList() {
    useTrackScreenVisit("meta insight list");

    const {t} = useTranslation();
    const navigate = useNavigate();
    const facebookConfig = useFacebookConfig();
    const readOnlyUser = useReadOnly();
    const filterOptionsQuery = useInsightFilterOptions();
    const hasConnectedAccount = Boolean(facebookConfig?.accessToken && facebookConfig?.adAccountId);

    const handleAdAccountAction = useCallback(() => {
        if (!hasConnectedAccount) {
            navigate("/system/metaConnector");
            return;
        }

        const adAccountId = facebookConfig?.adAccountId?.replace(/^act_/, "");
        if (!adAccountId) return;

        window.open(
            `https://adsmanager.facebook.com/adsmanager/manage/accounts?act=${adAccountId}`,
            "_blank",
            "noopener,noreferrer",
        );
    }, [facebookConfig?.adAccountId, hasConnectedAccount, navigate]);

    const columns = useMemo<ColumnDef<MetaInsight>[]>(() => [
        {
            id: "status",
            header: t("Status"),
            accessorFn: insight => insight.status,
            cell: ({row}) => <MetaInsightStatusCell status={row.original.status}/>,
            size: 90,
            meta: {defaultActive: true, isServerSortable: true, align: "center"},
        },
        {
            id: "campaignName",
            header: t("Campaign Name"),
            accessorFn: insight => insight.campaignName,
            cell: ({row}) => <Box>{row.original.campaignName ?? null}</Box>,
            size: 300,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "type",
            header: t("Type"),
            accessorFn: insight => insight.type,
            cell: ({row}) => <Box>{row.original.type ?? null}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "budget",
            header: t("Remaining Budget"),
            accessorFn: insight => insight.budget,
            cell: ({row}) => (
                <MetaInsightCurrencyCell
                    symbol={row.original.accountCurrencySymbol}
                    value={row.original.budget}
                    suffix={` / ${t("Day")}`}
                />
            ),
            size: 200,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "impressions",
            header: t("Impr."),
            accessorFn: insight => insight.impressions,
            cell: ({row}) => <MetaInsightMetricCell value={row.original.impressions}/>,
            size: 130,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "clicks",
            header: t("Clicks"),
            accessorFn: insight => insight.clicks,
            cell: ({row}) => <MetaInsightMetricCell value={row.original.clicks}/>,
            size: 120,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "ctr",
            header: t("CTR"),
            accessorFn: insight => insight.ctr,
            cell: ({row}) => <MetaInsightMetricCell value={row.original.ctr} suffix=" %"/>,
            size: 120,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "spend",
            header: t("Spent"),
            accessorFn: insight => insight.spend,
            cell: ({row}) => (
                <MetaInsightCurrencyCell
                    symbol={row.original.accountCurrencySymbol}
                    value={row.original.spend}
                />
            ),
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "conversions",
            header: t("Conversion short"),
            accessorFn: insight => insight.conversions,
            cell: ({row}) => <MetaInsightMetricCell value={row.original.conversions}/>,
            size: 130,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "costPerConversion",
            header: t("Cost Conversion short"),
            accessorFn: insight => insight.costPerConversion,
            cell: ({row}) => (
                <MetaInsightCurrencyCell
                    symbol={row.original.accountCurrencySymbol}
                    value={row.original.costPerConversion}
                />
            ),
            size: 160,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: insight => insight.id,
            cell: ({row}) => <Box>{row.original.id ?? null}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "updateDate",
            header: t("Last updated"),
            accessorFn: insight => insight.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true},
        },
    ], [t]);

    const controller = useServerTable<MetaInsight>({
        tableId: "marketing-meta-insights",
        columns,
        getRowId: insight => insight.id!,
        initialSorting: [{id: "id", desc: true}],
        refetchInterval: 700 * 1000,
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await MetaInsightsService.getInsights({
                query: searchQuery || undefined,
                advancedFilter: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    const accountActionLabel = hasConnectedAccount
        ? t("Go to ad account")
        : t("Connect ad account");

    return (
        <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ServerTableRailFilter
                                options={filterOptionsQuery.data ?? []}
                                disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                            />
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <RightSidebarActionButton
                                icon={hasConnectedAccount ? <OpenInNewIcon/> : <LinkOutlinedIcon/>}
                                label={accountActionLabel}
                                disabled={!hasConnectedAccount && readOnlyUser}
                                onClick={handleAdAccountAction}
                            />
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
}
